import React from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Flex,
  Text,
  SimpleGrid,
  Circle,
  Center,
  useTheme,
} from '@chakra-ui/react';
import { FaStar } from 'react-icons/fa';

import { generateSectionId } from '@rsc/ui-components/dept/utils/generateSectionId';
import Image from '@rsc/ui-components/tailwind/Image';
import Link from '@rsc/ui-components/tailwind/Link';
import SanityBackgroundImage from '../../../atoms/SanityBackgroundImage';

export default function EditorialIconStats({ block }) {
  const { headline, copy, badges, background } = block;
  const theme = useTheme();

  return (
    <Container
      {...generateSectionId(block)}
      px={{ base: '0', lg: '80px', '2xl': '120px' }}
      py={{ base: 14, lg: 0 }}
      pb={{ base: 0, lg: 20 }}
    >
      <SanityBackgroundImage
        h={{ base: 'auto', lg: '40rem' }}
        py={20}
        px={{ lg: '28' }}
        gradientGenerator={url =>
          `linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.60) 77.40%), url(${url})`
        }
        breakpointOverrides={{
          base: 'none',
        }}
        image={background}
        as={Flex}
        flexDir="column"
        align={{ base: 'center', lg: 'flex-start' }}
        justify="end"
        gap={6}
        borderRadius="3xl"
        overflow="hidden"
      >
        {headline && (
          <Text
            as="h2"
            textStyle="h1"
            color={{ lg: 'typography.lighter' }}
            textShadow={{ lg: '0px 4px 20px rgba(0, 0, 0, 0.9)' }}
          >
            {headline}
          </Text>
        )}
        {copy && (
          <Text
            color="typography.lighter"
            textShadow="0px 2px 15px  rgba(0, 0, 0, 1)"
          >
            {copy}
          </Text>
        )}

        <Image
          className="-mb-20 lg:hidden [&>img]:aria-hidden"
          sanityImage={background}
          sanityRole="presentation"
          srcSetBasisWidth={1000}
        />

        <SimpleGrid
          w="full"
          columns={{ base: 1, md: 2, xl: 3 }}
          gap={6}
          zIndex={1}
          px={{ base: 6, lg: 0 }}
        >
          {badges?.length > 0 &&
            badges.map(badge => (
              <Flex
                key={badge._key}
                as={Link}
                link={badge?.link}
                transition="filter 0.2s ease-in-out"
                _hover={{ filter: badge?.link ? 'brightness(1.05)' : 'none' }}
                _focus={{ filter: badge?.link ? 'brightness(1.05)' : 'none' }}
                bg="brand.sunny"
                bgImg={theme.images.circlesPattern}
                bgBlendMode="soft-light"
                bgSize="cover"
                bgPosition="center"
                p={6}
                borderRadius="3xl"
                align="center"
                gap={4}
              >
                <Circle as={Center} bg="brand.ink" h={12} w={12}>
                  {badge.icon?.asset ? (
                    <Image sanityImage={badge.icon} width="1.5rem" />
                  ) : (
                    <FaStar color="white" />
                  )}
                </Circle>
                <Text
                  fontSize="md"
                  textStyle="h4_5_6"
                  fontWeight="bold"
                  pr={{
                    base: 0,
                    xl: 8,
                  }}
                >
                  {badge.copy}
                </Text>
              </Flex>
            ))}
        </SimpleGrid>
      </SanityBackgroundImage>
    </Container>
  );
}

EditorialIconStats.propTypes = {
  block: PropTypes.shape({
    _key: PropTypes.string,
    _type: PropTypes.string,
    headline: PropTypes.string,
    copy: PropTypes.string,
    badges: PropTypes.arrayOf(
      PropTypes.shape({
        _key: PropTypes.string,
        copy: PropTypes.string,
      })
    ),
    background: PropTypes.shape({
      asset: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
  }).isRequired,
};
